import { useBankProfile } from '@contexts/BankProfileContext';
import { useFF } from '@hooks/useFF.ts';
import type { OPERATION_TYPE } from '@pages/batch-operations/batch-operations';
import { useMutation, useQuery } from '@tanstack/react-query';
import { cleanUpPayload } from '@utils/utils';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { format } from 'date-fns';
import { CC_FEATURE_FLAGS } from 'types/layout.ts';
import type { OperationStatus } from 'types/operations';

import { useAuth } from '../../hooks';
import type { PaginatedItems } from './common';
import type { NewPaymentPayload } from './types/createTransaction';
import type { Transaction } from './types/getTransactions';

export const STATEMENTS_PAGE_SIZE = 20;

const useCreateTransaction = () => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();
	const hasAddressAligned = useFF(CC_FEATURE_FLAGS.ADDRESS_ALIGNMENT);

	const createTransaction = async ({ data }: { data: NewPaymentPayload }) => {
		const updated = cleanUpPayload(data, hasAddressAligned);
		const response = await axios.post(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile?.id}/transactions`,
			updated,
			{
				headers: { Authorization: session?.tokenized },
			},
		);
		return response.data;
	};

	return useMutation({
		mutationFn: createTransaction,
		onSuccess: async (data) => data,
		onError: async (data: AxiosError<{ errors: Record<string, string> }>) => data,
	});
};

// ToDo: Refactor this to use object destructuring instead of array destructuring and have selected profile from hook?
const useTransactions = (
	customerId: string,
	page: number,
	limit = STATEMENTS_PAGE_SIZE,
	dateFrom?: string,
	dateTo?: string,
	type?: OPERATION_TYPE,
	documentNumber?: string,
	status?: OperationStatus[],
	senderIban?: string,
	beneficiaryIban?: string,
	beneficiaryName?: string,
	scheduled: boolean = false,
) => {
	const { session } = useAuth();

	const fetchTransactions = async (
		passedCustomerId?: string,
	): Promise<PaginatedItems<Transaction[]>> => {
		const params = {
			page,
			per_page: limit,
			sort: '-id',
			...(dateFrom ? { 'filter[created_at][from]': dateFrom } : {}),
			...(dateTo ? { 'filter[created_at][to]': dateTo } : {}),
			...(status?.length ? { 'filter[statuses]': status } : {}),
			...(type ? { 'filter[payment_type]': type } : {}),
			...(documentNumber ? { 'filter[document_number]': documentNumber } : {}),
			...(senderIban ? { 'filter[payer_account_number]': senderIban } : {}),
			...(beneficiaryIban
				? { 'filter[beneficiary_account_number]': beneficiaryIban }
				: {}),
			...(beneficiaryName ? { 'filter[beneficiary_account_name]': beneficiaryName } : {}),
			...(scheduled
				? { 'filter[execution_date][from]': format(new Date(), 'yyyy-MM-dd') }
				: {}),
		};

		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${passedCustomerId}/transactions`,
			{
				params,
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: [
			'transactions',
			customerId,
			page,
			limit,
			dateFrom,
			dateTo,
			type,
			documentNumber,
			status,
			senderIban,
			beneficiaryIban,
			beneficiaryName,
		],
		queryFn: () => fetchTransactions(customerId),
		enabled: !!customerId,
		select: (data) => ({
			...data,
			items: data?.items?.map((transaction) => ({
				...transaction,
				amount: transaction.amount / 100,
			})),
		}),
	});
};

function useDocumentNumber(accountId?: string | number) {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const fetchDocumentNumber = async (): Promise<{ documentNumber: string }> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile?.id}/transactions/document-number/${accountId}`,
			{
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['document-number', accountId],
		queryFn: fetchDocumentNumber,
		enabled: !!accountId && !!selectedProfile?.id,
	});
}

function useProofOfPayment(transactionId?: string) {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const fetchProofOfPayment = async () => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile.id}/transactions/${transactionId}/proof-of-payment`,
			{
				headers: { Authorization: session?.tokenized },
				responseType: 'blob',
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['proof-of-payment', transactionId],
		queryFn: () => fetchProofOfPayment(),
		enabled: false,
		refetchOnWindowFocus: false,
	});
}

const useDeleteTransaction = () => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const deleteTransaction = async (props: { transactionId: string }) => {
		const { transactionId } = props;

		const response = await axios.delete(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile?.id}/transactions/${transactionId}`,
			{
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useMutation({
		mutationFn: deleteTransaction,
		onSuccess: async (_data, { transactionId }) => ({ transactionId }),
		onError: async (data) => data,
	});
};

export {
	useCreateTransaction,
	useDeleteTransaction,
	useDocumentNumber,
	useProofOfPayment,
	useTransactions,
};
